import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useLang } from '../../context/LangContext';
import { fr } from '../../i18n/fr';
import { en } from '../../i18n/en';
import { de } from '../../i18n/de';

const T = { fr, en, de };
const EASE = [0.16, 1, 0.3, 1];

// ROT13-encoded — defeats static scrapers; decoded only in the browser.
// Public booking address (separate account from owner's personal Gmail).
const EMAIL_ENC = 'gfnatnzhfvp@tznvy.pbz';
const rot13 = s => s.replace(/[a-zA-Z]/g, c => {
  const b = c <= 'Z' ? 65 : 97;
  return String.fromCharCode(((c.charCodeAt(0) - b + 13) % 26) + b);
});

export default function Contact() {
  const { lang } = useLang();
  const t = T[lang]?.contact || T.fr.contact;
  const [revealed, setRevealed] = useState(false);
  const email = useMemo(() => rot13(EMAIL_ENC), []);
  const display = revealed ? email : email.replace(/(.{2}).+(@.+)/, '$1•••••$2');

  return (
    <section style={{ position: 'relative', minHeight: '100vh', background: 'var(--bg)', padding: '80px 56px 100px', overflow: 'hidden' }}>
      {/* Background photo */}
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        <img
          src="/TSANGA 7 exp.jpg"
          alt=""
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.12, filter: 'grayscale(0.6)', display: 'block' }}
        />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(4,4,10,0.4) 0%, rgba(4,4,10,0.85) 100%)' }} />
      </div>

      <div className="noise" aria-hidden="true" style={{ zIndex: 1 }} />

      {/* Chapter bar */}
      <motion.div
        initial={{ opacity: 0, y: -10 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ duration: 0.7, ease: EASE }}
        style={{
          position: 'relative', zIndex: 2,
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          paddingBottom: 18, borderBottom: '1px solid var(--line)', marginBottom: 56,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
          <span style={{ fontFamily: 'var(--display)', fontSize: 32, fontWeight: 300 }}>VI.</span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase' }}>Booking</span>
        </div>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.18em', color: 'var(--faint)', textTransform: 'uppercase' }}>
          Direct enquiries
        </span>
      </motion.div>

      {/* 2-col layout */}
      <div className="contact-grid" style={{ position: 'relative', zIndex: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56, alignItems: 'end' }}>

        {/* Giant title */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 1, ease: EASE }}
          style={{
            fontFamily: 'var(--display)', fontWeight: 200,
            fontSize: 'clamp(60px,12vw,200px)', lineHeight: 0.86,
            letterSpacing: '-0.02em', margin: 0,
          }}
        >
          <span style={{ display: 'block' }}>Get</span>
          <span style={{ display: 'block' }}>in</span>
          <span style={{ display: 'block', fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 300 }}>touch.</span>
        </motion.h2>

        {/* Glassmorphism panel */}
        <motion.div
          initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
          style={{
            border: '1px solid rgba(255,255,255,0.16)',
            padding: 40,
            background: 'rgba(4,4,10,0.75)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            display: 'flex', flexDirection: 'column', gap: 20,
          }}
        >
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.24em', color: 'var(--faint)', textTransform: 'uppercase' }}>
            {t.label}
          </span>

          <motion.a
            href={`mailto:${email}`}
            onMouseEnter={() => setRevealed(true)}
            onFocus={() => setRevealed(true)}
            aria-label="Send booking enquiry email to Tsanga"
            whileHover={{ color: 'rgba(255,255,255,0.65)' }}
            transition={{ duration: 0.2 }}
            style={{
              fontFamily: 'var(--display)', fontWeight: 300,
              fontSize: 'clamp(24px,3.5vw,48px)',
              color: '#fff', textDecoration: 'none',
              letterSpacing: '0.01em', lineHeight: 1.05,
              wordBreak: 'break-word',
              borderBottom: '1px solid rgba(255,255,255,0.3)',
              paddingBottom: 18,
              cursor: 'none',
            }}
          >
            {display}
          </motion.a>

          <p style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 17, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5, margin: 0 }}>
            {t.sub}
          </p>

          <div style={{
            paddingTop: 18, borderTop: '1px solid var(--line)',
            display: 'flex', flexWrap: 'wrap', gap: 18,
            fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.14em',
            textTransform: 'uppercase',
          }}>
            {[
              { label: 'IG @tsanga_music', href: 'https://www.instagram.com/tsanga_music/' },
              { label: 'SC tsanga', href: 'https://soundcloud.com/tsanga-music' },
              { label: 'BC tsanga', href: 'https://tsanga.bandcamp.com' },
            ].map(link => (
              <motion.a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ color: '#fff' }}
                transition={{ duration: 0.2 }}
                style={{ color: 'var(--faint)', textDecoration: 'none', cursor: 'none' }}
              >
                {link.label}
              </motion.a>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
